import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { enrollBegin, enrollConfirm, AuthError } from "../lib/auth";
import { Button, Card, Field, ErrorBanner, Spinner } from "../components";
import { theme, fontStack, radius } from "../theme";

/**
 * Enrôlement 2FA — 1er accès d'un admin fraîchement promu (isAdmin, sans TOTP).
 *
 * Le /v1/console/login le refuse (403 CONSOLE_TOTP_REQUIRED) tant que le TOTP
 * n'est pas activé ; Login détecte ce code et bascule ICI, en portant l'e-mail et
 * le mot de passe déjà saisis. On récupère un facteur 1 régulier (lib/auth,
 * enrollBegin) puis on suit /v1/console/totp/enroll → confirm.
 *
 * QR rendu en SVG INLINE (qrcode, pure JS bundlé) — compatible CSP stricte du
 * vhost admin (script-src 'self', aucun CDN) ; le secret est aussi affiché pour
 * la saisie manuelle si le QR n'est pas scannable.
 */
export function Enroll({
  email,
  password,
  onEnrolled,
  onCancel,
}: {
  email: string;
  password: string;
  onEnrolled: () => void;
  onCancel: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "scan" | "recovery">("loading");
  const [secret, setSecret] = useState("");
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Démarre l'enrôlement au montage : facteur 1 régulier ⇒ secret TOTP ⇒ QR.
  // L'effet POSTe (login + enroll) : il DOIT être idempotent. Le garde `useRef`
  // survit au double-invoke de StrictMode (même instance) — sans lui, deux
  // enrollBegin concurrents se courent sur l'upsert device de /auth/login.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const { otpauthUrl, secret } = await enrollBegin(email.trim(), password);
        const svg = await QRCode.toString(otpauthUrl, { type: "svg", margin: 1, width: 220 });
        setSecret(secret);
        setQrSvg(svg);
        setPhase("scan");
      } catch (err) {
        setError(messageFor(err));
      }
    })();
    // email/password sont figés pour la durée de l'écran (portés par Login).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const codes = await enrollConfirm(code.trim());
      setRecoveryCodes(codes);
      setPhase("recovery");
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <Card style={{ width: 440, maxWidth: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: fontStack.mono, color: theme.accent, fontSize: 13, letterSpacing: 1 }}>
            LIVEMESSAGE
          </div>
          <h1 style={{ fontFamily: fontStack.uiSemiBold, fontSize: 22, marginTop: 4 }}>
            {phase === "recovery" ? "Codes de secours" : "Activer la double authentification"}
          </h1>
          <p style={{ color: theme.ink3, fontSize: 13, marginTop: 6, wordBreak: "break-all" }}>
            {phase === "recovery"
              ? "Conservez ces codes AVANT de terminer."
              : `Compte ${email} · à faire une seule fois`}
          </p>
        </div>

        {error && <ErrorBanner message={error} />}

        {phase === "loading" && <Spinner label="Préparation de l'enrôlement…" />}

        {phase === "scan" && (
          <>
            <ol style={{ color: theme.ink2, fontSize: 13, lineHeight: 1.6, paddingLeft: 18, margin: "0 0 16px" }}>
              <li>Ouvrez votre application d'authentification (Google Authenticator, 1Password, Authy…).</li>
              <li>Scannez ce QR code, ou ajoutez un compte manuellement avec la clé ci-dessous.</li>
              <li>Saisissez le code à 6 chiffres généré pour confirmer.</li>
            </ol>

            {qrSvg && (
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: radius.field,
                  padding: 12,
                  width: 220,
                  margin: "0 auto 16px",
                  display: "flex",
                }}
                // QR généré localement à partir de l'URI otpauth:// (aucune entrée
                // utilisateur) — SVG inline, compatible CSP stricte.
                dangerouslySetInnerHTML={{ __html: qrSvg }}
              />
            )}

            <div style={{ marginBottom: 16 }}>
              <span style={{ display: "block", color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
                Clé de configuration (saisie manuelle)
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <code
                  style={{
                    flex: 1,
                    background: theme.bg,
                    border: `1px solid ${theme.line}`,
                    borderRadius: radius.field,
                    padding: "10px 12px",
                    fontFamily: fontStack.mono,
                    fontSize: 13,
                    wordBreak: "break-all",
                    color: theme.ink,
                  }}
                >
                  {secret}
                </code>
                <Button variant="ghost" onClick={() => copy(secret)}>
                  Copier
                </Button>
              </div>
            </div>

            <form onSubmit={confirm}>
              <Field
                label="Code à 6 chiffres"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
              <Button type="submit" disabled={busy || code.trim().length === 0} style={{ width: "100%", marginTop: 6 }}>
                {busy ? "…" : "Activer et confirmer"}
              </Button>
            </form>
          </>
        )}

        {phase === "recovery" && (
          <>
            <p style={{ color: theme.ink2, fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
              Ces codes remplacent votre application d'authentification en cas de perte. Chacun ne
              fonctionne <b>qu'une seule fois</b>. Ils ne seront plus jamais affichés.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                background: theme.bg,
                border: `1px solid ${theme.line}`,
                borderRadius: radius.field,
                padding: 14,
                marginBottom: 14,
              }}
            >
              {recoveryCodes.map((c) => (
                <code key={c} style={{ fontFamily: fontStack.mono, fontSize: 14, color: theme.ink }}>
                  {c}
                </code>
              ))}
            </div>
            <Button variant="ghost" onClick={() => copy(recoveryCodes.join("\n"))} style={{ width: "100%", marginBottom: 10 }}>
              Copier les codes
            </Button>
            <Button onClick={onEnrolled} style={{ width: "100%" }}>
              J'ai conservé mes codes — se connecter
            </Button>
          </>
        )}

        {phase !== "recovery" && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              width: "100%",
              marginTop: 12,
              background: "transparent",
              border: "none",
              color: theme.ink3,
              fontSize: 13,
              borderRadius: radius.pill,
            }}
          >
            ← Annuler
          </button>
        )}
      </Card>
    </div>
  );
}

function copy(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function messageFor(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case "INVALID_CREDENTIALS":
        return "E-mail ou mot de passe incorrect.";
      case "ADMIN_REQUIRED":
        return "Ce compte n'a pas accès à la console.";
      case "TOTP_ALREADY_ENABLED":
        return "La double authentification est déjà activée — revenez à la connexion.";
      case "TOTP_INVALID":
        return "Code incorrect. Vérifiez l'heure de votre téléphone et réessayez.";
      case "NO_ENROLLMENT":
        return "Enrôlement expiré, veuillez recommencer.";
      default:
        return err.message;
    }
  }
  return "Une erreur est survenue.";
}
